import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, ChevronLeft, ChevronRight, Flame, ShieldCheck, Truck } from "lucide-react";
import { api, imgFallback, imgSrc } from "@/lib/api";
import { tl } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/store/ProductCard";

const Tile = ({ to, img, title, text, cta, tone, fade, big, testId }) => (
  <Link to={to} data-testid={testId}
    className={`group relative flex overflow-hidden rounded-3xl ${tone} ${big ? "min-h-[400px] lg:row-span-2 lg:min-h-[560px]" : "min-h-[240px] lg:min-h-0"}`}>
    <img src={`${img}-720.webp`} srcSet={`${img}-720.webp 720w, ${img}.webp 1536w`} sizes={big ? "(min-width: 1024px) 56vw, 100vw" : "(min-width: 1024px) 44vw, 100vw"} alt="" loading="lazy" decoding="async"
      className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105 ${big ? "object-[70%_center]" : "object-right"}`} />
    <div className={`absolute inset-0 ${fade}`} />
    <div className={`relative z-10 flex flex-col p-7 sm:p-10 ${big ? "max-w-md justify-end sm:justify-center" : "max-w-[60%] justify-center"}`}>
      <h2 className={`font-display font-bold leading-[1.02] ${big ? "text-4xl sm:text-5xl lg:text-6xl" : "text-3xl sm:text-4xl"}`}>{title}</h2>
      <p className={`mt-3 font-medium ${big ? "text-base sm:text-lg" : "text-sm sm:text-base"} opacity-90`}>{text}</p>
      <span className={`mt-6 inline-flex w-fit items-center gap-2 font-semibold ${big ? "btn-press rounded-full bg-[hsl(var(--cream))] px-7 py-3 text-[hsl(var(--espresso))] group-hover:bg-primary" : "underline decoration-2 underline-offset-4"}`}>
        {cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

const KombeBanner = () => (
  <section className="container-x pt-4 lg:pt-6" data-testid="home-kombe-banner">
    <Link to="/kategori/kombe-cesitleri" className="group relative flex min-h-[440px] items-end overflow-hidden rounded-3xl bg-[hsl(var(--espresso))] text-[hsl(var(--cream))] sm:min-h-[480px] sm:items-center lg:min-h-[540px]">
      <picture>
        <source media="(max-width: 640px)" srcSet="/img/kombe-m.webp" />
        <img src="/img/kombe-1264.webp" srcSet="/img/kombe-828.webp 828w, /img/kombe-1264.webp 1264w" sizes="(min-width: 1280px) 1200px, 100vw" alt="Geleneksel Hatay kömbeleri"
          width="1264" height="848" fetchpriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover object-[75%_center] transition-transform duration-[1.4s] group-hover:scale-[1.03]" />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--espresso))] via-[hsl(var(--espresso))]/55 to-transparent sm:bg-gradient-to-r sm:via-[hsl(var(--espresso))]/70" />
      <div className="relative z-10 max-w-xl p-7 sm:p-12 lg:p-16">
        <p className="inline-flex rounded-full border border-primary/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary">Hatay'ın geleneksel lezzeti</p>
        <h1 className="mt-5 font-display text-4xl font-bold leading-[1.02] sm:text-5xl lg:text-6xl" data-testid="home-kombe-title">Geleneksel Hatay Kömbesi</h1>
        <p className="mt-4 text-base font-medium text-[hsl(var(--cream))]/85 sm:text-lg">1993'ten Beri En Taze Lezzetler</p>
        <span data-testid="home-kombe-cta" className="btn-press mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-bold text-[hsl(var(--espresso))] shadow-[0_10px_30px_rgba(239,165,48,0.35)] transition-colors group-hover:bg-[hsl(var(--cream))]">
          Kömbeleri Keşfet <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  </section>
);

const Hero = () => (
  <section className="container-x grid gap-4 pt-6 lg:grid-cols-[1.25fr_1fr] lg:pt-8" data-testid="home-hero">
    <Tile big to="/kategori/kuruyemisler" img="/img/hero-board" tone="bg-[hsl(var(--espresso))] text-[hsl(var(--cream))]" fade="bg-gradient-to-r from-[hsl(var(--espresso))] via-[hsl(var(--espresso))]/75 to-transparent max-sm:bg-gradient-to-t max-sm:via-[hsl(var(--espresso))]/60" testId="home-hero-main"
      title={<>1993'ten Beri<br />Taze Kavrulmuş</>} text="Antep fıstığından fındığa, her gün taze kavrulan kuruyemişler istediğiniz gramajda." cta="Kuruyemişleri Keşfet" />
    <Tile to="/kategori/kuru-meyveler" img="/img/hero-dried" tone="bg-primary text-[hsl(var(--espresso))]" fade="bg-gradient-to-r from-primary via-primary/70 to-transparent" testId="home-hero-dried"
      title="Kuru Meyveler" text="Güneşte kurumuş kayısı, incir ve dut." cta="Hemen İncele" />
    <Tile to="/kategori/ceviz-cesitleri" img="/img/hero-pistachio" tone="bg-secondary text-[hsl(var(--espresso))]" fade="bg-gradient-to-r from-secondary via-secondary/75 to-transparent" testId="home-hero-walnut"
      title="Ceviz & Fıstık" text="Markamıza adını veren lezzet." cta="Hemen İncele" />
  </section>
);

const CategoryCircles = ({ cats, products }) => {
  const imgFor = (c) => c.image || products.find((p) => (p.category_slugs || [p.category_slug]).includes(c.slug) && p.images?.[0])?.images?.[0];
  return (
    <section className="container-x mt-14" data-testid="home-categories">
      <div className="flex snap-x gap-6 overflow-x-auto pb-4 sm:justify-center sm:gap-10 [&::-webkit-scrollbar]:hidden">
        {cats.map((c) => (
          <Link key={c.slug} to={`/kategori/${c.slug}`} data-testid={`home-category-${c.slug}`} className="group flex w-28 flex-none snap-start flex-col items-center gap-3 text-center sm:w-32">
            <span className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-4 border-secondary bg-white shadow-[0_8px_24px_rgba(42,29,22,0.10)] transition-[border-color,transform] duration-300 group-hover:-translate-y-1 group-hover:border-primary sm:h-32 sm:w-32">
              <img src={imgSrc(imgFor(c), 320)} alt={c.name} loading="lazy" decoding="async" onError={imgFallback} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
            </span>
            <span className="text-sm font-semibold leading-tight group-hover:text-accent">{c.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const Wave = ({ className }) => (
  <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className={`block h-8 w-full sm:h-14 ${className}`} aria-hidden="true">
    <path d="M0 60V30C90 10 180 0 300 14s210 34 330 30 210-34 330-36 230 26 330 30 150-14 150-14v36z" />
  </svg>
);

const TABS = [["one-cikan", "Öne Çıkanlar"], ["cok-satan", "Çok Satanlar"], ["yeni", "Yeni Gelenler"]];

const Carousel = ({ items }) => {
  const ref = useRef();
  const scroll = (d) => ref.current?.scrollBy({ left: d * 520, behavior: "smooth" });
  return (
    <div className="relative">
      <div ref={ref} className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-6 [&::-webkit-scrollbar]:hidden" data-testid="home-carousel">
        <Link to="/urunler" data-testid="home-carousel-promo" className="group relative flex w-[220px] flex-none snap-start flex-col overflow-hidden rounded-2xl bg-[hsl(var(--espresso))] p-6 text-[hsl(var(--cream))] sm:w-[260px]">
          <img src="/img/promo-mix-720.webp" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
          <p className="relative font-display text-3xl font-bold leading-tight text-[hsl(var(--espresso))]">Gramajını<br />Sen Seç</p>
          <span className="relative mt-4 w-fit rounded-full bg-[hsl(var(--cream))] px-5 py-2 text-sm font-semibold text-[hsl(var(--espresso))] group-hover:bg-white">Tüm Ürünler</span>
        </Link>
        {items.map((p, i) => <ProductCard key={p.id} p={p} idx={i} compact />)}
      </div>
      {["-left-4", "-right-4"].map((pos, i) => (
        <button key={pos} onClick={() => scroll(i ? 1 : -1)} aria-label={i ? "Sonraki" : "Önceki"} data-testid={`home-carousel-${i ? "next" : "prev"}`}
          className={`absolute top-1/2 ${pos} hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg transition-transform hover:scale-110 md:flex`}>
          {i ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      ))}
    </div>
  );
};

const FeaturedSection = ({ lists }) => {
  const [tab, setTab] = useState("one-cikan");
  return (
    <section className="mt-16" data-testid="home-featured">
      <Wave className="fill-primary" />
      <div className="bg-primary pb-16 pt-6 text-[hsl(var(--espresso))]">
        <div className="container-x">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="font-display text-4xl font-bold sm:text-5xl">Sıradaki favorini bul</h2>
              <p className="mt-2 text-base font-medium opacity-80">Öne çıkanlar, en çok satanlar ya da yeni gelenler — canın ne çekiyorsa.</p>
            </div>
            <div className="flex gap-6" role="tablist">
              {TABS.map(([k, l]) => (
                <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`home-tab-${k}`}
                  className={`border-b-[3px] pb-1.5 text-base font-semibold transition-[border-color,opacity] ${tab === k ? "border-[hsl(var(--espresso))]" : "border-transparent opacity-60 hover:opacity-100"}`}>{l}</button>
              ))}
            </div>
          </div>
          <Carousel items={lists[tab] || []} />
        </div>
      </div>
      <Wave className="rotate-180 fill-primary" />
    </section>
  );
};

const GramBand = () => (
  <section className="container-x mt-16" data-testid="home-gram-band">
    <div className="grid items-center gap-8 overflow-hidden rounded-3xl bg-[hsl(var(--espresso))] p-8 text-[hsl(var(--cream))] sm:p-12 lg:grid-cols-2">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Dökme kuruyemiş</p>
        <h2 className="mt-3 font-display text-4xl font-bold leading-tight sm:text-5xl">İstediğin kadar al,<br />taze taze ye.</h2>
        <p className="mt-4 max-w-md text-[hsl(var(--cream))]/75">Tüm dökme ürünlerimizi 100 gramdan başlayarak dilediğiniz gramajda sipariş edebilirsiniz. Siparişiniz tartılarak, özenle paketlenir.</p>
        <Link to="/urunler" data-testid="home-gram-cta" className="btn-press mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 font-semibold text-[hsl(var(--espresso))] hover:bg-[hsl(var(--cream))]">Alışverişe Başla <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
        {["100 g", "250 g", "500 g", "1 kg"].map((g, i) => (
          <div key={g} className="flex aspect-[4/3] flex-col items-center justify-center rounded-2xl border-2 border-[hsl(var(--caramel))]/50 bg-white/5 transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-primary hover:text-[hsl(var(--espresso))]" style={{ transitionDelay: `${i * 30}ms` }}>
            <span className="font-display text-3xl font-bold">{g}</span>
            <span className="mt-1 text-xs opacity-70">{i === 3 ? "en avantajlı" : "paket seçeneği"}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Trust = () => {
  const { settings } = useStore();
  const items = [
    [Award, "1993'ten Beri", "Otuz yılı aşkın tecrübe"],
    [Flame, "Taze Kavrulmuş", "Küçük partilerle, her gün"],
    [Truck, "Hızlı Kargo", (settings?.free_shipping_threshold ?? 3000) > 0 ? `${tl(settings?.free_shipping_threshold ?? 3000)} üzeri ücretsiz` : "Kargo bedava"],
    [ShieldCheck, "Güvenli Ödeme", "Kart veya Havale / EFT"],
  ];
  return (
    <section className="container-x mt-16 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="home-trust">
      {items.map(([Icon, t, d]) => (
        <div key={t} className="flex items-center gap-4 rounded-2xl bg-muted p-5">
          <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-secondary text-[hsl(var(--espresso))]"><Icon className="h-5 w-5" /></span>
          <div><p className="font-display text-lg font-bold leading-tight">{t}</p><p className="text-xs text-muted-foreground">{d}</p></div>
        </div>
      ))}
    </section>
  );
};

export default function Home() {
  const [cats, setCats] = useState([]);
  const [lists, setLists] = useState({});
  const [all, setAll] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data.filter((c) => !c.parent_slug)));
    Promise.all([
      api.get("/products", { params: { featured: true } }),
      api.get("/products", { params: { sort: "cok_satan" } }),
      api.get("/products", { params: { sort: "yeni" } }),
    ]).then(([f, b, n]) => {
      setAll(b.data);
      setLists({ "one-cikan": f.data.length ? f.data : b.data.slice(0, 12), "cok-satan": b.data.slice(0, 12), yeni: n.data.slice(0, 12) });
    });
  }, []);
  return (
    <div>
      <KombeBanner />
      <Hero />
      <CategoryCircles cats={cats} products={all} />
      <FeaturedSection lists={lists} />
      <GramBand />
      <Trust />
    </div>
  );
}
